import React, { useEffect, useMemo, useState } from "react";
import { IconChevronDown, IconSortBoth, IconSortUp } from "../components/ui/icons";
import FilterBar from "../components/FilterBar";
import Card from "../components/ui/Card";
import { Skeleton } from "../components/ui/Skeleton";
import { EmptyState, ErrorState } from "../components/ui/StateBlock";
import { useApiData } from "../hooks/useApiData";
import { useWarehouses } from "../hooks/useWarehouses";
import { unwrapArray } from "../utils/api";
import { exportSectionsToPDF, exportToExcel, printSections } from "../utils/exportUtils";
import { todayInputValue } from "../utils/date";

const COLUMNS = [
  { key: "artikal", label: "Artikal", align: "left", width: "40%" },
  { key: "kolicina", label: "Količina", align: "right", width: "24%" },
  { key: "vrednost", label: "Vrednost", align: "right", width: "36%" },
];

// Numbers are displayed with a thousands separator ("3.800.273,00"); exports stay raw.
const fmt2 = (v) =>
  (parseFloat(v) || 0).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export default function ReportTable() {
  // Draft - changes while you pick filters.
  const [type, setType] = useState("sve");
  const [startDate, setStartDate] = useState(todayInputValue);
  const [endDate, setEndDate] = useState(todayInputValue);
  const [selectedWarehouses, setSelectedWarehouses] = useState([]);

  // Applied - this builds the fetch path, it changes only on a click of "Prikaži".
  const [appliedType, setAppliedType] = useState("sve");
  const [appliedStartDate, setAppliedStartDate] = useState(todayInputValue);
  const [appliedEndDate, setAppliedEndDate] = useState(todayInputValue);
  const [appliedWarehouse, setAppliedWarehouse] = useState("");

  const [sortConfig, setSortConfig] = useState({ key: "artikal", direction: "asc" });

  const { warehouses } = useWarehouses();

  const [settingsApplied, setSettingsApplied] = useState(false);
  const { data: appSettingsRaw, error: appSettingsError } = useApiData("/settings_get.php");
  useEffect(() => {
    // open the gate even when the settings do not arrive (server error) - otherwise it stays on the spinner forever
    if (appSettingsRaw !== null || appSettingsError) {
      if (appSettingsRaw?.success && appSettingsRaw.default_magacin) {
        const ids = appSettingsRaw.default_magacin.split(",").filter(Boolean);
        setSelectedWarehouses(ids);
        setAppliedWarehouse(ids.join(","));
      }
      setSettingsApplied(true);
    }
  }, [appSettingsRaw, appSettingsError]);

  const handleApply = () => {
    setAppliedType(type);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);
    setAppliedWarehouse(selectedWarehouses.join(","));
  };

  const path = useMemo(() => {
    if (!settingsApplied) return null;
    const params = new URLSearchParams({ tip: appliedType, od: appliedStartDate, do: appliedEndDate });
    if (appliedWarehouse) params.append("warehouse", appliedWarehouse);
    return `/summary_items.php?${params.toString()}`;
  }, [appliedType, appliedStartDate, appliedEndDate, appliedWarehouse, settingsApplied]);

  const { data: raw, loading, error } = useApiData(path);

  const sortedData = useMemo(() => {
    const items = unwrapArray(raw);
    const { key, direction } = sortConfig;
    const arr = [...items];
    arr.sort((a, b) => {
      if (key === "artikal") {
        return direction === "asc" ? a.artikal.localeCompare(b.artikal) : b.artikal.localeCompare(a.artikal);
      }
      const diff = parseFloat(a[key]) - parseFloat(b[key]);
      return direction === "asc" ? diff : -diff;
    });
    return arr;
  }, [raw, sortConfig]);

  const requestSort = (key) => {
    setSortConfig((prev) =>
      prev.key === key ? { key, direction: prev.direction === "asc" ? "desc" : "asc" } : { key, direction: "asc" }
    );
  };

  const totalQuantity = sortedData.reduce((sum, p) => sum + parseFloat(p.kolicina || 0), 0);
  const totalValue = sortedData.reduce((sum, p) => sum + parseFloat(p.vrednost || 0), 0);

  // One data set for all three exports (numbers stay numbers; the exports format them and add the bold "Ukupno" row).
  const exportSection = () => ({
    head: ["Artikal", "Količina", "Vrednost"],
    body: sortedData.map((p) => [p.artikal, parseFloat(p.kolicina) || 0, parseFloat(p.vrednost) || 0]),
    totalRow: ["Ukupno", totalQuantity, totalValue],
  });

  const handleExportExcel = () =>
    exportToExcel(
      sortedData.map((p) => ({ Artikal: p.artikal, Količina: parseFloat(p.kolicina) || 0, Vrednost: parseFloat(p.vrednost) || 0 })),
      "izvestaj.xlsx",
      "Izveštaj",
      { title: "Izveštaj" }
    );

  const handleExportPdf = () => exportSectionsToPDF({ title: "Izveštaj", sections: [exportSection()], filename: "izvestaj.pdf" });

  const handlePrint = () => printSections({ title: "Izveštaj", sections: [exportSection()] });

  return (
    <div className="flex flex-col gap-4">
      <FilterBar
        type={type}
        setType={setType}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        selectedWarehouses={selectedWarehouses}
        setSelectedWarehouses={setSelectedWarehouses}
        warehouses={warehouses}
        onApply={handleApply}
        onExportExcel={handleExportExcel}
        onExportPdf={handleExportPdf}
        onExportPrint={handlePrint}
        exportDisabled={sortedData.length === 0}
      />

      <Card padded={false}>
        <table className="w-full table-fixed border-collapse text-sm">
          <thead>
            <tr>
              {COLUMNS.map((col) => (
                <th
                  key={col.key}
                  scope="col"
                  aria-sort={
                    sortConfig.key === col.key ? (sortConfig.direction === "asc" ? "ascending" : "descending") : "none"
                  }
                  style={{ width: col.width }}
                  className={`sticky top-0 border-b border-slate-200 bg-slate-50 px-2 py-2 sm:px-3 text-xs font-medium text-slate-500
                    first:rounded-tl-[9px] last:rounded-tr-[9px] dark:border-slate-800 dark:bg-slate-900 dark:text-slate-400
                    ${col.align === "right" ? "text-right" : "text-left"}`}
                >
                  <button
                    type="button"
                    onClick={() => requestSort(col.key)}
                    className={`inline-flex items-center gap-1 rounded-sm transition-colors hover:text-slate-900 dark:hover:text-slate-100 ${
                      col.align === "right" ? "flex-row-reverse" : ""
                    }`}
                  >
                    {col.label}
                    <SortIcon active={sortConfig.key === col.key} direction={sortConfig.direction} />
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
            {loading &&
              sortedData.length === 0 &&
              Array.from({ length: 6 }, (_, i) => (
                <tr key={i}>
                  <td className="px-3 py-2.5">
                    <Skeleton className="h-3.5 w-3/4" />
                  </td>
                  <td className="px-3 py-2.5">
                    <Skeleton className="ml-auto h-3.5 w-12" />
                  </td>
                  <td className="px-3 py-2.5">
                    <Skeleton className="ml-auto h-3.5 w-16" />
                  </td>
                </tr>
              ))}
            {!loading && error && (
              <tr>
                <td colSpan={3}>
                  <ErrorState onRetry={handleApply} />
                </td>
              </tr>
            )}
            {!loading && !error && sortedData.length === 0 && (
              <tr>
                <td colSpan={3}>
                  <EmptyState title="Nema podataka za izabrani period" hint="Promeni period ili magacin i klikni Prikaži." />
                </td>
              </tr>
            )}
            {sortedData.map((p, i) => (
              <tr key={`${p.artikal}-${i}`} className={`transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50 ${loading ? "opacity-60" : ""}`}>
                <td className="break-words px-2 py-2 text-slate-800 dark:text-slate-100 sm:px-3">{p.artikal}</td>
                <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200 sm:px-3">
                  {fmt2(p.kolicina)}
                </td>
                <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums text-slate-700 dark:text-slate-200 sm:px-3">
                  {fmt2(p.vrednost)}
                </td>
              </tr>
            ))}
          </tbody>
          {sortedData.length > 0 && (
            <tfoot>
              <tr className="border-t border-slate-200 bg-slate-50 font-semibold dark:border-slate-800 dark:bg-slate-900">
                <td className="rounded-bl-[9px] px-2 py-2.5 text-slate-900 sm:px-3 dark:text-slate-50">Ukupno</td>
                <td className="whitespace-nowrap px-2 py-2.5 text-right tabular-nums text-slate-900 dark:text-slate-50 sm:px-3">
                  {fmt2(totalQuantity)}
                </td>
                <td className="whitespace-nowrap rounded-br-[9px] px-2 py-2.5 text-right tabular-nums text-slate-900 dark:text-slate-50 sm:px-3">
                  {fmt2(totalValue)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </Card>
    </div>
  );
}

function SortIcon({ active, direction }) {
  if (!active) return <IconSortBoth className="text-sm text-slate-300 dark:text-slate-600" aria-hidden="true" />;
  return direction === "asc" ? (
    <IconSortUp className="text-sm text-primary dark:text-primary-light" aria-hidden="true" />
  ) : (
    <IconChevronDown className="text-sm text-primary dark:text-primary-light" aria-hidden="true" />
  );
}
